import { Controller } from "@hotwired/stimulus"
import { getCart, totalCount, addToCart, removeFromCart, incrementItem, decrementItem, clearCart, replaceCart } from "../lib/cart_store"
import { checkoutAttempt, clearCheckoutAttempt } from "../lib/checkout_attempt"
import { computePromoDiscount } from "../lib/promotions"
import { buildCartWhatsAppUrl } from "../lib/whatsapp"
import { formatCurrency, conditionLabel, escapeHtml } from "../lib/format"
import { coverToneFor } from "../lib/cover_tone"
import { track, SHELF_ADD, WHATSAPP_ORDER } from "../lib/analytics"

// Owns the "Sua Estante" drawer, the header cart-count badge, and the toast
// region — one instance lives on <body> (see layouts/application.html.erb).
// Reacts to `cart:changed` (dispatched by cart_store on every mutation, in
// any tab), `toast:push` and `cart:book-added` (dispatched by whichever
// controller just added a book).
export default class extends Controller {
  static targets = [
    "count", "drawer", "unitsLabel", "items",
    "discountRow", "discountValue", "subtotal", "checkoutBtn", "clearBtn",
    "toastRegion", "fulfillment", "eventSelect", "checkoutStatus"
  ]

  static values = {
    promoActive: Boolean,
    promoGenreIds: Array,
    promoFreeMaxPrice: Number,
    promoPaidPerFree: Number,
    promoEndDate: String,
    pickupOrderUrl: String,
    quoteUrl: String,
    // [{ id, label }] of the events currently open for pickup, already
    // filtered and date-formatted server-side (EventBanner#pickable /
    // #pickup_label). Empty when the feature is off or nothing qualifies —
    // and empty is the whole signal: there is no separate flag to read,
    // because with no event to pick, delivery is the only way to receive.
    pickupOptions: Array
  }

  connect() {
    this.onCartChanged = () => this.render()
    this.onToastPush = (event) => this.pushToast(event.detail.msg)
    this.onBookAdded = (event) => this.openAfterAdd(event.detail.source)
    this.onBeforeCache = () => this.close()

    window.addEventListener("cart:changed", this.onCartChanged)
    window.addEventListener("toast:push", this.onToastPush)
    window.addEventListener("cart:book-added", this.onBookAdded)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)

    // Delivery is the default because it is the mode that always works: a
    // customer who never touches the selector sends exactly the message the
    // storefront sent before this existed.
    this.fulfillment = "entrega"
    this.selectedEventId = this.pickupOptionsValue[0]?.id ?? null
    this.submittingPickup = false
    this.pickupCompleted = false

    // Rendered once, not from render(): it does not depend on the cart, and
    // rebuilding it on every add/remove would throw away the customer's
    // choice mid-order.
    this.renderFulfillment()
    this.render()
  }

  disconnect() {
    window.removeEventListener("cart:changed", this.onCartChanged)
    window.removeEventListener("toast:push", this.onToastPush)
    window.removeEventListener("cart:book-added", this.onBookAdded)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
  }

  open() {
    if (this.drawerTarget.open) return
    this.drawerTarget.showModal()
    this.drawerTarget.classList.add("open")
  }

  close(event) {
    event?.preventDefault()
    this.drawerTarget.close()
    this.drawerTarget.classList.remove("open")
  }

  backdrop(event) {
    if (event.target !== this.drawerTarget) return
    const bounds = this.drawerTarget.getBoundingClientRect()
    if (event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom) this.close()
  }

  trapFocus(event) {
    if (event.key !== "Tab") return
    const controls = [...this.drawerTarget.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]')]
      .filter(node => node.getClientRects().length > 0)
    const first = controls[0]
    const last = controls.at(-1)
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  openAfterAdd(source) {
    if (this.pickupOptionsValue.length === 0) return

    // A native modal stays in the top layer, above the cart drawer, even
    // when the drawer has a higher z-index.
    source?.closest("dialog[open]")?.close()
    this.open()
    this.fulfillmentTarget.querySelector("input[name='cart-fulfillment']")?.focus()
  }

  // Used by the plain "Comprar" buttons on the catalog grid cards — always
  // adds/increments, no local toggle state (unlike book_purchase_controller,
  // used where the button must flip into a qty-stepper once it's in the cart).
  add(event) {
    const p = event.params
    addToCart({
      id: p.id,
      title: p.title,
      author: p.author,
      price: p.price,
      internal_code: p.internalCode || null,
      condition: p.condition,
      image_url: p.imageUrl || null,
      stock: p.stock,
      genreIds: (p.genreIds || []).map(String)
    })
    track(SHELF_ADD, { code: p.internalCode || null, title: p.title })
    window.dispatchEvent(new CustomEvent("toast:push", { detail: { msg: `"${p.title}" adicionado à sua estante` } }))
    window.dispatchEvent(new CustomEvent("cart:book-added", { detail: { source: event.currentTarget } }))
  }

  increment(event) {
    incrementItem(event.params.id)
  }

  decrement(event) {
    decrementItem(event.params.id)
  }

  remove(event) {
    removeFromCart(event.params.id)
  }

  clear() {
    clearCart()
  }

  async checkout() {
    const cart = structuredClone(getCart())
    if (cart.length === 0 || this.submittingPickup) return
    const fulfillment = this.fulfillmentChoice()
    let pickupPayload = null
    if (fulfillment?.kind === "evento") {
      const name = this.fulfillmentTarget.querySelector("[name='pickup-name']")
      const phone = this.fulfillmentTarget.querySelector("[name='pickup-phone']")
      if (!name.reportValidity() || !phone.reportValidity()) return
      let digits = phone.value.replace(/\D/g, "")
      if (digits.startsWith("55") && digits.length >= 12) digits = digits.slice(2)
      pickupPayload = {
        customer_name: name.value.trim().replace(/\s+/g, " "), customer_phone: `55${digits}`,
        event_id: String(this.selectedEventId),
        items: cart.map(({ id, quantity }) => ({ book_id: String(id), quantity }))
          .sort((a, b) => a.book_id.localeCompare(b.book_id))
      }
    }

    this.submittingPickup = true
    this.pickupCompleted = false
    this.checkoutBtnTarget.disabled = true
    this.checkoutBtnTarget.textContent = "Conferindo estante…"
    this.checkoutStatusTarget.textContent = ""
    this.showPickupResult("")
    // Create the tab during the click, before awaiting the server.
    const tab = window.open("", "_blank")
    if (tab) {
      tab.opener = null
      tab.document.body.textContent = "Conferindo seu pedido…"
    }
    try {
      const quote = await this.postJSON(this.quoteUrlValue, {
        items: cart.map(({ id, quantity }) => ({ book_id: String(id), quantity }))
      })
      if (JSON.stringify(getCart()) !== JSON.stringify(cart)) {
        this.checkoutStatusTarget.textContent = "Sua estante mudou. Confira os itens e finalize novamente."
        tab?.close()
        return
      }
      const beforeTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0) - this.computePromo(cart).discountTotal
      const changed = this.cartSignature(cart) !== this.cartSignature(quote.items) ||
        Math.round(beforeTotal * 100) !== Math.round(Number(quote.total_amount) * 100)
      this.promoActiveValue = quote.promotion.active
      this.promoGenreIdsValue = quote.promotion.genreIds
      this.promoFreeMaxPriceValue = quote.promotion.freeItemMaxPrice
      this.promoPaidPerFreeValue = quote.promotion.paidPerFree
      this.promoEndDateValue = quote.promotion.endDate || ""
      replaceCart(quote.items)
      if (changed) {
        this.checkoutStatusTarget.textContent = "Atualizamos os preços, a promoção ou a disponibilidade. Confira sua estante e finalize novamente."
        tab?.close()
        return
      }

      if (pickupPayload) {
        pickupPayload.expected_total = quote.total_amount
        pickupPayload.idempotency_key = await checkoutAttempt("pickup", pickupPayload)
        const result = await this.postJSON(this.pickupOrderUrlValue, pickupPayload)
        if (tab) tab.location.href = result.whatsapp_url
        this.showPickupResult(`Pedido ${escapeHtml(result.code)} salvo. A disponibilidade será confirmada pela equipe. <a href="${escapeHtml(result.whatsapp_url)}" target="_blank" rel="noopener noreferrer">Enviar mensagem no WhatsApp</a>.`, true)
        clearCheckoutAttempt("pickup")
        this.pickupCompleted = true
      } else {
        const url = buildCartWhatsAppUrl(quote.items, this.computePromo(quote.items), fulfillment)
        if (tab) tab.location.href = url
        this.checkoutStatusTarget.innerHTML = `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">Enviar mensagem no WhatsApp</a>`
      }
      const properties = { units: cart.reduce((sum, item) => sum + item.quantity, 0), total_cents: Math.round(Number(quote.total_amount) * 100) }
      if (fulfillment) properties.pickup = fulfillment.kind
      track(WHATSAPP_ORDER, properties)
    } catch (error) {
      tab?.close()
      this.checkoutStatusTarget.textContent = error instanceof TypeError
        ? "Não foi possível conectar. Tente novamente."
        : (error.message || "Não foi possível finalizar o pedido. Tente novamente.")
    } finally {
      this.submittingPickup = false
      this.checkoutBtnTarget.disabled = getCart().length === 0
      this.checkoutBtnTarget.textContent = this.pickupCompleted ? "Fazer outro pedido" : "Finalizar Pedido"
    }
  }

  cartSignature(items) {
    return JSON.stringify(items.map(item => ({
      id: String(item.id), title: item.title, author: item.author, price: Number(item.price),
      quantity: item.quantity, stock: item.stock, condition: item.condition,
      genreIds: item.genreIds.map(String).sort()
    })))
  }

  async postJSON(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
      body: JSON.stringify(payload)
    })
    const result = response.headers.get("Content-Type")?.includes("application/json") ? await response.json() : {}
    if (!response.ok) throw new Error(result.error || "Não foi possível finalizar o pedido. Tente novamente.")
    return result
  }

  showPickupResult(content, success = false) {
    const node = this.fulfillmentTarget.querySelector("[data-pickup-result]")
    if (!node) return
    node.innerHTML = content
    node.classList.toggle("is-success", success)
  }

  // null when no event is open for pickup: with delivery as the only mode
  // there was no question to answer, so the order message says nothing about
  // it — exactly as it did before this feature.
  fulfillmentChoice() {
    if (this.pickupOptionsValue.length === 0) return null
    if (this.fulfillment !== "evento") return { kind: "entrega" }

    const event = this.pickupOptionsValue.find((option) => String(option.id) === String(this.selectedEventId))
    return event ? { kind: "evento", label: event.label } : { kind: "entrega" }
  }

  selectFulfillment(event) {
    this.fulfillment = event.target.value
    this.pickupCompleted = false
    this.checkoutBtnTarget.textContent = "Finalizar Pedido"
    if (this.hasEventSelectTarget) {
      this.eventSelectTarget.disabled = this.fulfillment !== "evento"
    }
    this.fulfillmentTarget.querySelector("[data-pickup-contact]")?.classList.toggle("u-hidden", this.fulfillment !== "evento")
    this.showPickupResult("")
  }

  selectEvent(event) {
    this.selectedEventId = event.target.value
  }

  // Deliberately worded as a preference, not a reservation: payment happens at
  // pickup and the team still confirms every order by hand, so the site must
  // not promise a copy is being held.
  renderFulfillment() {
    if (!this.hasFulfillmentTarget) return
    if (this.pickupOptionsValue.length === 0) {
      this.fulfillmentTarget.innerHTML = ""
      return
    }

    const options = this.pickupOptionsValue
      .map((option) => `<option value="${escapeHtml(String(option.id))}">${escapeHtml(option.label)}</option>`)
      .join("")

    this.fulfillmentTarget.innerHTML = `
      <fieldset class="ff-set">
        <legend>Como você prefere receber?</legend>
        <label class="ff-opt">
          <input type="radio" name="cart-fulfillment" value="evento" data-action="change->cart#selectFulfillment">
          <span>Retirar em evento</span>
        </label>
        <select class="ff-event" aria-label="Escolher o evento" disabled
                data-cart-target="eventSelect" data-action="change->cart#selectEvent">
          ${options}
        </select>
        <div class="ff-contact u-hidden" data-pickup-contact>
          <label>Seu nome<input type="text" name="pickup-name" autocomplete="name" maxlength="120" minlength="2" required></label>
          <label>WhatsApp com DDD<input type="tel" name="pickup-phone" autocomplete="tel" inputmode="tel" required placeholder="(93) 99999-9999"></label>
          <small>O pedido será salvo antes de abrir o WhatsApp. A equipe confirmará a disponibilidade.</small>
        </div>
        <label class="ff-opt">
          <input type="radio" name="cart-fulfillment" value="entrega" checked data-action="change->cart#selectFulfillment">
          <span>Combinar recebimento</span>
        </label>
      </fieldset>
      <p class="ff-result" role="status" data-pickup-result></p>
    `
  }

  computePromo(cart) {
    const settings = {
      active: this.promoActiveValue,
      genreIds: this.promoGenreIdsValue.map(String),
      freeItemMaxPrice: this.promoFreeMaxPriceValue,
      paidPerFree: this.promoPaidPerFreeValue,
      endDate: this.promoEndDateValue || null
    }
    const lines = cart.map((item) => ({ id: item.id, price: item.price, quantity: item.quantity, genreIds: item.genreIds }))
    return computePromoDiscount(settings, lines)
  }

  pushToast(msg) {
    if (!this.hasToastRegionTarget) return

    const existing = this.toastRegionTarget.querySelector(`[data-toast-msg="${CSS.escape(msg)}"]`)
    if (existing) existing.remove()

    const toast = document.createElement("div")
    toast.className = "toast"
    toast.dataset.toastMsg = msg
    toast.innerHTML = `
      <svg class="t-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M20 6 9 17l-5-5"/></svg>
      <span>${escapeHtml(msg)}</span>
      <button class="t-action" type="button" data-action="click->cart#openFromToast">Ver</button>
    `
    this.toastRegionTarget.appendChild(toast)
    setTimeout(() => toast.remove(), 3800)
  }

  openFromToast() {
    this.open()
  }

  render() {
    const cart = getCart()
    const count = totalCount()

    if (this.hasCountTarget) {
      this.countTarget.textContent = count
      this.countTarget.classList.toggle("u-hidden", count === 0)
    }
    if (this.hasUnitsLabelTarget) {
      this.unitsLabelTarget.textContent = `${count} ${count === 1 ? "item" : "itens"}`
    }

    const promo = this.computePromo(cart)
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const total = subtotal - promo.discountTotal

    if (this.hasItemsTarget) {
      this.itemsTarget.innerHTML = cart.length === 0
        ? `<div class="drawer-empty">
             <span class="serif">Sua estante está vazia</span>
             Adicione livros do acervo — quando encontrar o seu, garanta logo.
           </div>`
        : cart.map((item) => this.itemTemplate(item, promo)).join("")
    }

    if (this.hasDiscountRowTarget) {
      this.discountRowTarget.classList.toggle("u-hidden", promo.discountTotal <= 0)
    }
    if (this.hasDiscountValueTarget) {
      this.discountValueTarget.textContent = `-${formatCurrency(promo.discountTotal)}`
    }
    if (this.hasSubtotalTarget) {
      this.subtotalTarget.textContent = formatCurrency(total)
    }
    if (this.hasCheckoutBtnTarget) {
      this.checkoutBtnTarget.disabled = this.submittingPickup || cart.length === 0
    }
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.classList.toggle("u-hidden", cart.length === 0)
    }
  }

  itemTemplate(item, promo) {
    const freeUnits = Math.min(promo.freeUnitsByLineId.get(item.id) ?? 0, item.quantity)
    const coverTone = coverToneFor(item.internal_code)
    const cover = item.image_url
      ? `<img src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item.title)}" loading="lazy">`
      : `<span>Nº ${item.internal_code ?? "—"}</span>`
    const meta = `${escapeHtml(item.author)} · ${conditionLabel(item.condition)}${item.internal_code != null ? ` · Nº ${item.internal_code}` : ""}`
    const promoBadge = freeUnits > 0
      ? `<div class="badge promo">${freeUnits > 1 ? `${freeUnits} grátis (promoção)` : "1 grátis (promoção)"}</div>`
      : ""

    return `
      <div class="drawer-item">
        <div class="di-cover${item.image_url ? "" : ` tone-${coverTone}`}">${cover}</div>
        <div class="di-body">
          <div class="di-title">${escapeHtml(item.title)}</div>
          <div class="di-meta">${meta}</div>
          ${promoBadge}
          <div class="di-qty">
            <button type="button" data-action="click->cart#decrement" data-cart-id-param="${item.id}" aria-label="Diminuir quantidade">−</button>
            <span class="n">${item.quantity}</span>
            <button type="button" data-action="click->cart#increment" data-cart-id-param="${item.id}" ${item.quantity >= item.stock ? "disabled" : ""} aria-label="Aumentar quantidade">+</button>
          </div>
          <button class="di-remove" type="button" data-action="click->cart#remove" data-cart-id-param="${item.id}">Remover</button>
        </div>
        <span class="di-price">${formatCurrency(item.price * item.quantity - freeUnits * item.price)}</span>
      </div>
    `
  }
}
